import type { TournamentLocation } from "./locations";
import type { TournamentInformation } from "./tournament-information";
import { formatLocal } from "./time";
import { wallClock } from "./wall-clock";

/**
 * The tournament as an ENTITY (V2-RESEARCH.md §4, decisions D3-D6, D16).
 *
 * Until v2 this app had exactly one tournament, and it was not a thing: it was
 * the singleton `TournamentDO("main")` plus a settings row. This module is
 * the shape the registry stores, the rules for the one string that reaches a
 * URL (the slug), the naming of the Durable Objects that hang off a
 * tournament, and the calendar arithmetic the front page groups by. Pure,
 * shared by the Worker (registry, routes) and the UI (console, front page),
 * tested in `test/tournament.test.ts`.
 *
 * Two facts everything else follows from:
 *
 *   THE EXISTING EVENT IS TOURNAMENT `main`, FOREVER. Its Durable Object was
 *   named "main" before a registry existed, its fields' objects are named
 *   `field-<id>`, and both hold a live event's data. Nothing moves: the
 *   registry gets a row whose id is "main" and whose slug is whatever the
 *   owner chooses at adoption, and every naming function below carries that
 *   one exception explicitly.
 *
 *   FOR EVERY OTHER TOURNAMENT, THE ID IS THE SLUG. One string names the
 *   Durable Object, the API path (`/api/t/<id>/…`) and the page path
 *   (`/t/<slug>/…`), so no request ever needs a lookup to go from one to the
 *   other. The price is that a slug is immutable once the tournament is
 *   published (D4): it is printed on QR codes and typed into OBS.
 */

export const LEGACY_TOURNAMENT_ID = "main";

/**
 * The "tournament" a platform-level request belongs to: the registry, the
 * superadmin surface, sessions, health. The audit log has written `"*"` for
 * those lines since the log existed; the router now names every request's
 * tournament, and this is the name for "none of them".
 */
export const PLATFORM_ID = "*";

/**
 * The lifecycle (V2 Phase 4, and `registration` since 2026-09-11): a draft is
 * invisible; a tournament taking REGISTRATION is public and its door is open
 * to clubs, who enter from their club page with the players they are
 * bringing; published is the event proper (the door shut, the teams stay);
 * archived is final. Every public read treats registration exactly as
 * published - the only thing the status changes is whether
 * `POST /api/t/:tid/register/:club` is answered.
 */
export const TOURNAMENT_STATUSES = ["draft", "registration", "published", "archived"] as const;
export type TournamentStatus = (typeof TOURNAMENT_STATUSES)[number];

export function isTournamentStatus(v: unknown): v is TournamentStatus {
  return typeof v === "string" && (TOURNAMENT_STATUSES as readonly string[]).includes(v);
}

/**
 * The message `TournamentDO.assertWritable()` throws with, and the error code
 * the router turns it into (`shared/error-codes.ts`). One string on purpose:
 * the check lives at the storage boundary, inside the Durable Object, and the
 * only thing that crosses the RPC boundary is an Error's message.
 */
export const ARCHIVED_ERROR = "tournament-archived";
export const ROSTER_LOCKED_ERROR = "roster-locked";

/**
 * The moves an ORGANISER may make (research §4.7, §8 Q4 and Q13): open
 * registration on a draft or publish it outright, close registration
 * (publish), re-open it on a published event, archive a published event,
 * un-archive to edit it again. Back to draft is not theirs - a public
 * address may already be on a QR code or in a club's hands - and the
 * superadmin may move anything anywhere.
 */
export function organiserMayMove(from: TournamentStatus, to: TournamentStatus): boolean {
  return (
    (from === "draft" && (to === "registration" || to === "published")) ||
    (from === "registration" && to === "published") ||
    (from === "published" && (to === "registration" || to === "archived")) ||
    (from === "archived" && to === "published")
  );
}

/** Staff links expire three days after the event's last day (AUTH.md §3):
 *  `endDate` is zone-less, so the end of that day in the reader's zone. */
export function grantExpiryFor(endDate: string, now: number): number {
  const end = new Date(`${endDate}T23:59:59`).getTime();
  const horizon = (Number.isFinite(end) ? end : now) + 3 * 86_400_000;
  return Math.max(horizon, now + 86_400_000);
}

/** Bounds the validator and the forms share. A name longer than a board
 *  header can show is a name nobody will read. */
export const MAX_TOURNAMENT_NAME = 60;
export const MAX_TOURNAMENT_SHORT = 12;
/** City and venue. */
export const MAX_TOURNAMENT_PLACE = 60;
/** An IANA zone name ("Europe/Kyiv"); the longest real one is 32 characters. */
export const MAX_TOURNAMENT_TIMEZONE = 64;
export const SLUG_MIN = 3;
export const SLUG_MAX = 40;

/**
 * The registry row. Canonical in `PlatformDO`, mirrored as `meta` inside the
 * tournament's own `TournamentDO` (D6) so the board bundle and the
 * archived-refuses-writes check never need a second hop.
 *
 * Dates are ZONE-LESS `YYYY-MM-DD` strings, the same convention as every
 * schedule time in this app (`shared/time.ts`): a tournament happens on the
 * wall calendar at the fields. Deadlines use the event's `timezone`, never
 * the reader's zone. Without a zone they are advisory and not enforced.
 */
export interface Tournament {
  /** `LEGACY_TOURNAMENT_ID`, or the slug. Also the Durable Object name. */
  id: string;
  slug: string;
  name: string;
  /** For a band that cannot fit the name: "UON 2026". */
  short?: string;
  startDate: string;
  endDate: string;
  /** Optional HH:mm on endDate in the event timezone. Absent means end of day. */
  endTime?: string;
  timezone?: string;
  registrationCloses?: string;
  rosterLocks?: string;
  entryReview?: boolean;
  city?: string;
  venue?: string;
  locations?: TournamentLocation[];
  information?: TournamentInformation;
  status: TournamentStatus;
  createdAt: number;
  /** Label of the principal that created (or adopted) it, like `Grant.createdBy`. */
  createdBy: string;
  updatedAt: number;
}

/**
 * What a front-page card says beyond the name and the dates (refinements,
 * 2026-09-09): counts from the rows, nothing a spectator could not read off
 * the board itself. `played` against `games` is the one that moves during an
 * event.
 */
export interface TournamentStats {
  teams: number;
  players: number;
  divisions: number;
  games: number;
  played: number;
}

/** What a spectator's request gets: the row minus who created it. */
export type TournamentSummary = Omit<Tournament, "createdBy"> & {
  /** Response-only event-local minute, computed by the Worker, never stored. */
  wallNow?: string;
};

export function publicTournament(t: Tournament): TournamentSummary {
  const { createdBy: _private, ...rest } = t;
  return rest;
}

/** What the console (or `tools/dev-links.ts`) sends to create or adopt one.
 *  `status` is optional: create defaults to draft, adoption to published. */
export interface TournamentInput {
  slug: string;
  name: string;
  short?: string;
  startDate: string;
  endDate: string;
  /** Optional HH:mm on endDate in the event timezone. Absent means end of day. */
  endTime?: string;
  timezone?: string;
  registrationCloses?: string;
  rosterLocks?: string;
  entryReview?: boolean;
  city?: string;
  venue?: string;
  locations?: TournamentLocation[];
  information?: TournamentInformation;
  status?: TournamentStatus;
}

/**
 * What `POST /api/tournaments/:id/clone` takes (V2 Phase 6): the NEW row's
 * fields, and whether the teams come over with their rosters. The copy is
 * always a draft; a `status` in the body is ignored.
 */
export interface CloneInput extends TournamentInput {
  copyRosters: boolean;
}

/** A PUT body: only the fields being changed. `slug` is honoured while the
 *  tournament is a draft and refused (`slug-frozen`) afterwards. */
export type TournamentPatch = Partial<TournamentInput>;

type EntryRules = Pick<Tournament, "status" | "timezone" | "registrationCloses" | "rosterLocks">;

/** Equality closes the door: a 09:00 deadline is closed from 09:00 onward.
 * Missing server time fails closed only when a deadline is enforceable. */
function beforeDeadline(
  meta: EntryRules,
  deadline: string | undefined,
  wallNow: string | undefined,
): boolean {
  return !meta.timezone || !deadline || (wallNow !== undefined && wallNow < deadline);
}

export function doorOpen(meta: EntryRules, wallNow: string | undefined): boolean {
  return meta.status === "registration" && beforeDeadline(meta, meta.registrationCloses, wallNow);
}

/** Published events still allow roster corrections until their own lock.
 * Organiser overrides are an authorization decision at the storage boundary. */
export function rosterOpen(meta: EntryRules, wallNow: string | undefined): boolean {
  return meta.status !== "archived" && beforeDeadline(meta, meta.rosterLocks, wallNow);
}

/** Strict wall minute; reject offsets, seconds, rollover dates and 24:00.
 * Validate the calendar independently of the machine's daylight-saving zone. */
export function isLocalDateTime(value: unknown): value is string {
  if (typeof value !== "string" || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return false;
  const year = Number(value.slice(0, 4));
  const month = Number(value.slice(5, 7));
  const day = Number(value.slice(8, 10));
  const date = new Date(0);
  date.setUTCFullYear(year, month - 1, day);
  return (
    date.getUTCFullYear() === year && date.getUTCMonth() === month - 1 &&
    date.getUTCDate() === day && Number(value.slice(11, 13)) < 24 && Number(value.slice(14)) < 60
  );
}

// ---------- slugs ----------

/**
 * Words a slug may not be. The list is defensive: `/t/<slug>` never sits at
 * the root, so today nothing here can actually collide with a page, but
 * `main` is the legacy id (a second tournament called "main" would name the
 * same Durable Object) and the rest are the segments the router owns, kept
 * free for the day a short `/<slug>` alias is wanted. Anything shorter than
 * `SLUG_MIN` ("t" itself) needs no entry: the shape rule refuses it first,
 * and the test that walks this list treats such an entry as dead.
 */
export const RESERVED_SLUGS = [
  "api",
  "admin",
  "team",
  "super",
  "overlay",
  "bridge",
  "board",
  "spirit",
  "rules",
  "regulations",
  "login",
  "invite",
  "main",
  "new",
] as const;

/** Lowercase ASCII letters, digits and single hyphens, never at an edge. */
const SLUG_RE = /^[a-z0-9](?:[a-z0-9]|-(?=[a-z0-9]))*$/;

export type SlugProblem = "shape" | "reserved";

/** Why a slug is refused, or null when it is fine. The two reasons are kept
 *  apart because they get different sentences: "letters, digits and hyphens"
 *  against "that word is taken by the app". */
export function slugProblem(v: unknown): SlugProblem | null {
  if (typeof v !== "string" || v.length < SLUG_MIN || v.length > SLUG_MAX) return "shape";
  if (!SLUG_RE.test(v)) return "shape";
  if ((RESERVED_SLUGS as readonly string[]).includes(v)) return "reserved";
  return null;
}

/** Shape only. A reserved word is still a well-formed slug; `slugProblem`
 *  tells the two apart. */
export function isSlug(v: unknown): v is string {
  const problem = slugProblem(v);
  return problem === null || problem === "reserved";
}

/**
 * A tournament id as it arrives in a URL: the legacy id, or a usable slug.
 * Used on every `:id` / `:tid` param before it reaches a Durable Object name.
 */
export function isTournamentId(v: unknown): v is string {
  return v === LEGACY_TOURNAMENT_ID || slugProblem(v) === null;
}

/**
 * Ukrainian letters as the 2010 national transliteration standard writes them
 * (the passport table). Only for SUGGESTING a slug from a name: the organiser
 * sees the result in the form and may retype it, so the table can afford to
 * skip the standard's positional rules for є/ї/й/ю/я at a word start.
 */
const UK_LATIN: Readonly<Record<string, string>> = {
  а: "a",
  б: "b",
  в: "v",
  г: "h",
  ґ: "g",
  д: "d",
  е: "e",
  є: "ye",
  ж: "zh",
  з: "z",
  и: "y",
  і: "i",
  ї: "yi",
  й: "y",
  к: "k",
  л: "l",
  м: "m",
  н: "n",
  о: "o",
  п: "p",
  р: "r",
  с: "s",
  т: "t",
  у: "u",
  ф: "f",
  х: "kh",
  ц: "ts",
  ч: "ch",
  ш: "sh",
  щ: "shch",
  ь: "",
  ю: "yu",
  я: "ya",
  "'": "",
  "’": "",
};

/**
 * A slug to propose for a name: "Ukrainian Open Nationals 2026" becomes
 * `ukrainian-open-nationals-2026`, «Кубок Львова 2027» becomes
 * `kubok-lvova-2027`. Empty when nothing survives, which the form shows as a
 * placeholder rather than as a slug. Never validated here: the result goes
 * back through `slugProblem` like anything typed by hand.
 */
export function slugSuggestion(name: string): string {
  const latin = [...name.toLowerCase()].map((ch) => UK_LATIN[ch] ?? ch).join("");
  return latin
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, SLUG_MAX)
    .replace(/-+$/, "");
}

// ---------- Durable Object names ----------

/**
 * The name of the `FieldDO` holding one field's live game (D5).
 *
 * The legacy tournament keeps `field-<id>`: those objects hold the existing
 * event's state, undo stacks and open sockets, and renaming an object is
 * copying it. Every other tournament gets a name that cannot collide with a
 * legacy one WHATEVER the ids contain: a legacy field id may itself carry
 * hyphens (`fieldIdFrom` writes "show-field"), so `field-<tid>-<fieldId>`
 * would have let tournament "show" field "field" name the same object as
 * legacy field "show-field". A different prefix and a different separator
 * make the two families disjoint by construction, and the test says so.
 */
export function fieldDoName(tournamentId: string, fieldId: string): string {
  if (tournamentId === LEGACY_TOURNAMENT_ID) return `field-${fieldId}`;
  return `t/${tournamentId}/field/${fieldId}`;
}

// ---------- the calendar ----------

const DATE_ONLY_RE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `YYYY-MM-DD`, and a date that exists: "2026-02-30" is refused. */
export function isDateOnly(v: unknown): v is string {
  if (typeof v !== "string") return false;
  const m = DATE_ONLY_RE.exec(v);
  if (m === null) return false;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const d = new Date(year, month - 1, day);
  return d.getFullYear() === year && d.getMonth() === month - 1 && d.getDate() === day;
}

/** Today as the wall calendar has it, from LOCAL date parts: the same rule
 *  as every schedule comparison in this app (ARCHITECTURE.md invariant 11).
 *  `toISOString()` would move a late-evening reader east of Greenwich into
 *  tomorrow. */
export function todayLocal(now: Date = new Date()): string {
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

export const TOURNAMENT_PHASES = ["now", "upcoming", "past"] as const;
export type TournamentPhase = (typeof TOURNAMENT_PHASES)[number];

/** Calendar phase, derived from the event's local date and optional closing
 * minute. Date inputs are instants converted into the event timezone; strings
 * are already event-local (date-only callers retain the inclusive-day rule). */
export function phaseOf(
  t: Pick<Tournament, "startDate" | "endDate" | "endTime" | "timezone">,
  now: string | Date,
): TournamentPhase {
  const local = typeof now === "string" ? now : wallClock(t.timezone, now.getTime()) ?? formatLocal(now);
  const today = local.slice(0, 10);
  if (today < t.startDate) return "upcoming";
  if (today > t.endDate) return "past";
  if (t.endTime && local >= `${t.endDate}T${t.endTime}`) return "past";
  return "now";
}
